import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import { Button, makeStyles } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import { colors } from 'theme';

import ReceptionForm from 'components/Forms/ReceptionForm';
import RequiredFieldsLegend from 'components/Forms/RequiredFieldsLegend';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import PageContent from 'components/Generic/PageContent';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { Routes } from 'routes';
import { putReception } from 'services/apiOperations';
import { DialogWidth, ReceptionForm as ReceptionFormType } from 'types';
import { formatReceptionBeforePost, formatReceptionForEdit } from 'utils/normalizer';
import { useReceptionById } from 'hooks';
import Dialog from 'components/Dialog';
import { goBack } from 'utils/history';

const DUPLICATE_RECEPTION_VIOLATION = 'Reception already exists with the given date/stream/client';

const useStyles = makeStyles({
  buttonBar: {
    display: 'flex',
    justifyContent: 'center',
    '& > *': {
      margin: '0 10px',
    },
  },
});

type SubmissionError = {
  status: number;
  response: {
    body: {
      violations?: {
        code: string;
        message: string;
        propertyPath: string;
      }[];
    };
  };
};

const ReceptionEdit: React.FC = () => {
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const classes = useStyles();

  const history = useHistory();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const { receptionId } = useParams<{ receptionId: string }>();
  const location = useLocation<{ referer?: string }>();
  const backRoute = location.state?.referer ?? generatePath(Routes.ReceptionShow, { receptionId });
  const { loading, value: reception } = useReceptionById(receptionId);

  const onSubmit = async (reception: ReceptionFormType) => {
    if (isSubmitting || !sortingCenter) {
      return;
    }

    const formattedReception = {
      ...formatReceptionBeforePost(reception),
      sortingCenter: sortingCenter['@id'],
    };

    setIsSubmitting(true);
    putReception(formattedReception, receptionId)
      .then(() => {
        notify({
          message: t('receptionForm.edition.success'),
          severity: 'success',
        });
        goBack(backRoute, history);
        setIsSubmitting(false);
      })
      .catch((e: SubmissionError) => {
        setIsSubmitting(false);
        if (e.status === 412) {
          if (e.response.body.violations) {
            const duplicateViolation = e.response.body.violations.find(
              violation => violation.message === DUPLICATE_RECEPTION_VIOLATION,
            );
            if (duplicateViolation) {
              setDuplicateModalOpen(true);
              return;
            }
          }
        }
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };

  return (
    <>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isDuplicateModalOpen}
        title={t('receptions.create.duplicateModal.title')}
        width={DialogWidth.DEFAULT}
        renderContent={() => (
          <div className={classes.buttonBar}>
            <Button
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              onClick={() => {
                goBack(Routes.Receptions, history);
              }}
            >
              {t('receptions.create.duplicateModal.backToList')}
            </Button>
            <Button
              endIcon={<EditIcon />}
              variant="outlined"
              onClick={() => {
                setDuplicateModalOpen(false);
              }}
              style={{
                background: colors.grey900,
                color: 'white',
              }}
            >
              {t('receptions.create.duplicateModal.edit')}
            </Button>
          </div>
        )}
      />
      <PageContent title={t('receptions.edit.title')} backRoute={backRoute} withButtonBar>
        {!loading && reception && <ReceptionForm onSubmit={onSubmit} reception={formatReceptionForEdit(reception)} />}
        <RequiredFieldsLegend />
      </PageContent>
      <BottomButtonBar
        formName="reception_form"
        previousStepLabel={t('receptions.edit.cancelButtonLabel')}
        onClickPrevious={() => goBack(backRoute, history)}
        disableNext={isSubmitting}
        nextStepIcon={<CheckCircleOutlineOutlinedIcon />}
        nextStepLabel={t('receptions.edit.editButtonLabel')}
      />
    </>
  );
};
export default ReceptionEdit;
